import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserPlus, BadgeCheck, CalendarClock, Mail, Search, ShieldCheck, ArrowRight, Award, Share2, Link2 } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { Reveal } from "../components/Reveal";

const STEP_ICONS = [UserPlus, BadgeCheck, CalendarClock, Mail];
const BEN_ICONS = [Search, ShieldCheck, Award];

const C = {
  sk: {
    kicker: "Pre profesionálov a remeselníkov",
    title: "Buď v databáze SauberEko® – firmy ťa oslovia samy",
    lead: "Prestaň sa ponúkať. Získaj certifikát alebo preverenie SauberEko®, nastav si od kedy si voľný a overené firmy ťa nájdu podľa tvojich zručností – zváranie, nerez, cleanroom, jazyky a ďalšie.",
    cta: "Zaregistrovať sa", cta2: "Pozrieť Sieň slávy",
    stepsTitle: "Ako to funguje",
    steps: [
      ["Zaregistruj sa", "Vytvor si účet za pár minút a vyplň žiadosť."],
      ["Získaj certifikát alebo preverenie", "Zlatý certifikát Sa.Eko.Prs. alebo preverenie online Sa.Eko.Verified na základe fotiek a videí tvojej práce."],
      ["Nastav „Voľný od“", "V profile zadáš, od kedy môžeš nastúpiť. Tvoje zručnosti sa zobrazia firmám."],
      ["Firmy ťa oslovia", "Správu od firmy dostaneš priamo do svojho účtu a upozornenie e-mailom."],
    ],
    benTitle: "Prečo SauberEko®",
    ben: [
      ["Hľadajú ťa podľa zručností", "Firmy filtrujú presne to, čo vieš – nie podľa životopisu, ale podľa overených zručností."],
      ["Overená odbornosť", "Tvoja odbornosť je zapísaná vo verejnej Sieni slávy a overiteľná cez QR kód."],
      ["Lepšie ohodnotenie", "Certifikovaný profesionál už nie je za cenu začiatočníka. Firmy platia za skutočnú úroveň."],
    ],
    fee: "Registračný poplatok 59 € (vrátane DPH). Pre už certifikovaných v Sieni slávy: prihlás sa e-mailom a ako heslo zadaj číslo svojho certifikátu.",
    endTitle: "Nechaj firmy, nech hľadajú teba",
    metaDesc: "Buď v databáze SauberEko® a nechaj firmy, nech ťa oslovia samy. Certifikát alebo preverenie online, zručnosti v Sieni slávy a pracovné ponuky priamo do tvojho účtu.",
  },
  en: {
    kicker: "For professionals and tradespeople",
    title: "Be in the SauberEko® database – companies will contact you",
    lead: "Stop chasing jobs. Get a SauberEko® certificate or verification, set when you are available and verified companies will find you by your skills – welding, stainless steel, cleanroom, languages and more.",
    cta: "Register now", cta2: "View the Hall of Fame",
    stepsTitle: "How it works",
    steps: [
      ["Register", "Create your account in minutes and fill in the application."],
      ["Get certified or verified", "Gold certificate Sa.Eko.Prs. or online verification Sa.Eko.Verified based on photos and videos of your work."],
      ["Set “Available from”", "In your profile you set when you can start. Your skills become visible to companies."],
      ["Companies contact you", "Messages from companies arrive directly in your account, with an e-mail notification."],
    ],
    benTitle: "Why SauberEko®",
    ben: [
      ["Found by your skills", "Companies filter exactly what you can do – verified skills, not just a CV."],
      ["Verified expertise", "Your expertise is listed in the public Hall of Fame and verifiable by QR code."],
      ["Better pay", "A certified professional is no longer paid like a beginner. Companies pay for real level."],
    ],
    fee: "Registration fee €59 (VAT incl.). Already certified in the Hall of Fame? Sign in with your e-mail and use your certificate number as password.",
    endTitle: "Let companies look for you",
    metaDesc: "Be in the SauberEko® database and let companies contact you. Certificate or online verification, skills in the Hall of Fame and job offers directly in your account.",
  },
  de: {
    kicker: "Für Fachkräfte und Handwerker",
    title: "Sei in der SauberEko®-Datenbank – Firmen sprechen dich an",
    lead: "Hör auf, dich anzubieten. Hol dir ein SauberEko®-Zertifikat oder eine Prüfung, gib an, ab wann du verfügbar bist, und geprüfte Firmen finden dich nach deinen Fähigkeiten – Schweißen, Edelstahl, Reinraum, Sprachen und mehr.",
    cta: "Jetzt registrieren", cta2: "Hall of Fame ansehen",
    stepsTitle: "So funktioniert es",
    steps: [
      ["Registrieren", "Konto in wenigen Minuten erstellen und Antrag ausfüllen."],
      ["Zertifikat oder Prüfung", "Goldenes Zertifikat Sa.Eko.Prs. oder Online-Prüfung Sa.Eko.Verified anhand von Fotos und Videos deiner Arbeit."],
      ["„Verfügbar ab“ setzen", "Im Profil gibst du an, ab wann du anfangen kannst. Firmen sehen deine Fähigkeiten."],
      ["Firmen sprechen dich an", "Nachrichten von Firmen kommen direkt in dein Konto, mit E-Mail-Benachrichtigung."],
    ],
    benTitle: "Warum SauberEko®",
    ben: [
      ["Gefunden nach Fähigkeiten", "Firmen filtern genau das, was du kannst – geprüfte Fähigkeiten statt nur Lebenslauf."],
      ["Geprüfte Fachkompetenz", "Deine Kompetenz steht in der öffentlichen Hall of Fame und ist per QR-Code prüfbar."],
      ["Bessere Bezahlung", "Eine zertifizierte Fachkraft wird nicht mehr wie ein Anfänger bezahlt."],
    ],
    fee: "Registrierungsgebühr 59 € (inkl. MwSt.). Bereits in der Hall of Fame? Mit E-Mail anmelden und die Zertifikatsnummer als Passwort verwenden.",
    endTitle: "Lass Firmen nach dir suchen",
    metaDesc: "Sei in der SauberEko®-Datenbank und lass Firmen dich ansprechen. Zertifikat oder Online-Prüfung, Fähigkeiten in der Hall of Fame und Jobangebote direkt in deinem Konto.",
  },
  pl: {
    kicker: "Dla fachowców i rzemieślników",
    title: "Bądź w bazie SauberEko® – firmy same się do ciebie odezwą",
    lead: "Przestań szukać ofert. Zdobądź certyfikat lub weryfikację SauberEko®, ustaw od kiedy jesteś dostępny, a sprawdzone firmy znajdą cię według umiejętności – spawanie, stal nierdzewna, cleanroom, języki i więcej.",
    cta: "Zarejestruj się", cta2: "Zobacz Galerię Sław",
    stepsTitle: "Jak to działa",
    steps: [
      ["Zarejestruj się", "Załóż konto w kilka minut i wypełnij wniosek."],
      ["Certyfikat lub weryfikacja", "Złoty certyfikat Sa.Eko.Prs. lub weryfikacja online Sa.Eko.Verified na podstawie zdjęć i filmów twojej pracy."],
      ["Ustaw „Dostępny od“", "W profilu podajesz, od kiedy możesz zacząć. Firmy widzą twoje umiejętności."],
      ["Firmy się odezwą", "Wiadomości od firm trafiają prosto na twoje konto, z powiadomieniem e-mail."],
    ],
    benTitle: "Dlaczego SauberEko®",
    ben: [
      ["Znajdą cię po umiejętnościach", "Firmy filtrują dokładnie to, co potrafisz – zweryfikowane umiejętności, nie tylko CV."],
      ["Zweryfikowana fachowość", "Twoja fachowość jest w publicznej Galerii Sław i do sprawdzenia kodem QR."],
      ["Lepsze wynagrodzenie", "Certyfikowany fachowiec nie jest już opłacany jak początkujący."],
    ],
    fee: "Opłata rejestracyjna 59 € (z VAT). Jesteś już w Galerii Sław? Zaloguj się e-mailem, a jako hasło podaj numer certyfikatu.",
    endTitle: "Niech firmy szukają ciebie",
    metaDesc: "Bądź w bazie SauberEko® i pozwól firmom się do ciebie odezwać. Certyfikat lub weryfikacja online, umiejętności w Galerii Sław i oferty pracy prosto na twoje konto.",
  },
  fr: {
    kicker: "Pour les professionnels et artisans",
    title: "Soyez dans la base SauberEko® – les entreprises vous contactent",
    lead: "Arrêtez de chercher. Obtenez un certificat ou une vérification SauberEko®, indiquez votre disponibilité et des entreprises vérifiées vous trouvent selon vos compétences – soudage, inox, salle blanche, langues et plus.",
    cta: "S'inscrire", cta2: "Voir le Hall of Fame",
    stepsTitle: "Comment ça marche",
    steps: [
      ["Inscrivez-vous", "Créez votre compte en quelques minutes et remplissez la demande."],
      ["Certificat ou vérification", "Certificat or Sa.Eko.Prs. ou vérification en ligne Sa.Eko.Verified sur la base de photos et vidéos de votre travail."],
      ["Indiquez « Disponible dès »", "Dans votre profil, indiquez quand vous pouvez commencer. Vos compétences sont visibles des entreprises."],
      ["Les entreprises vous contactent", "Les messages arrivent directement dans votre compte, avec notification par e-mail."],
    ],
    benTitle: "Pourquoi SauberEko®",
    ben: [
      ["Trouvé selon vos compétences", "Les entreprises filtrent exactement ce que vous savez faire – des compétences vérifiées, pas seulement un CV."],
      ["Expertise vérifiée", "Votre expertise figure au Hall of Fame public et se vérifie par QR code."],
      ["Meilleure rémunération", "Un professionnel certifié n'est plus payé comme un débutant."],
    ],
    fee: "Frais d'inscription 59 € (TVA incl.). Déjà certifié au Hall of Fame ? Connectez-vous avec votre e-mail et votre numéro de certificat comme mot de passe.",
    endTitle: "Laissez les entreprises vous trouver",
    metaDesc: "Soyez dans la base SauberEko® et laissez les entreprises vous contacter. Certificat ou vérification en ligne, compétences au Hall of Fame et offres directement dans votre compte.",
  },
};

const setMeta = (name, attr, content) => {
  let el = document.head.querySelector(`meta[${attr}='${name}']`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
};

const Steps = ({ t }) => (
  <div className="grid md:grid-cols-4 gap-4">
    {t.steps.map(([h, d], i) => {
      const Icon = STEP_ICONS[i];
      return (
        <Reveal key={h} delay={i * 0.08}>
          <div className="h-full border border-white/10 bg-[#0b0b0a] p-6 hover:border-[#D4AF37]/50 transition-colors" data-testid={`pro-step-${i}`}>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-9 h-9 rounded-full border border-[#D4AF37]/60 text-[#D4AF37] flex items-center justify-center text-sm font-bold">{i + 1}</span>
              <Icon size={20} className="text-[#D4AF37]" />
            </div>
            <p className="text-white font-display text-lg">{h}</p>
            <p className="text-white/55 text-sm mt-2 leading-relaxed">{d}</p>
          </div>
        </Reveal>
      );
    })}
  </div>
);

const SHARE_T = { sk: ["Zdieľaj s kamarátom", "Skopírovať odkaz", "Skopírované"], en: ["Share with a friend", "Copy link", "Copied"], de: ["Mit Freunden teilen", "Link kopieren", "Kopiert"], pl: ["Udostępnij znajomemu", "Kopiuj link", "Skopiowano"], fr: ["Partager avec un ami", "Copier le lien", "Copié"] };

const ShareBar = ({ lang, title }) => {
  const [copied, setCopied] = useState(false);
  const st = SHARE_T[lang] || SHARE_T.sk;
  const sl = ["sk", "en", "de"].includes(lang) ? lang : "en";
  const url = `${window.location.origin}/api/share/pre-profesionalov?lang=${sl}`;
  const copy = () => { navigator.clipboard?.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1800); };
  const btn = "inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 text-sm text-white/75 hover:border-[#D4AF37]/60 hover:text-white transition-colors";
  return (
    <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="pro-share">
      <span className="text-white/45 text-sm mr-1 inline-flex items-center gap-2"><Share2 size={15} className="text-[#D4AF37]" /> {st[0]}</span>
      <a href={`https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`} target="_blank" rel="noreferrer" data-testid="pro-share-whatsapp" className={btn}>WhatsApp</a>
      <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} target="_blank" rel="noreferrer" data-testid="pro-share-facebook" className={btn}>Facebook</a>
      <button onClick={copy} data-testid="pro-share-copy" className={btn}><Link2 size={14} /> {copied ? st[2] : st[1]}</button>
    </div>
  );
};

export default function PreProfesionalov() {
  const { lang } = useLang();
  const t = C[lang] || C.sk;
  useEffect(() => {
    document.title = `${t.title} | SauberEko®`;
    setMeta("description", "name", t.metaDesc);
    setMeta("og:title", "property", t.title);
    setMeta("og:description", "property", t.metaDesc);
    setMeta("og:image", "property", `${window.location.origin}/og-pre-profesionalov${lang === "en" ? "-en" : lang === "de" ? "-de" : ""}.jpg`);
  }, [t, lang]);

  return (
    <div className="min-h-screen bg-[#050505] text-white pt-28 pb-24" data-testid="pre-profesionalov-page">
      <div className="max-w-6xl mx-auto px-6">
        <Reveal>
          <div className="flex items-center gap-3 text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-6"><span className="h-px w-10 bg-[#D4AF37]/60" />{t.kicker}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl" data-testid="pro-title">{t.title}</h1>
          <p className="text-white/60 text-base md:text-lg mt-6 max-w-2xl leading-relaxed">{t.lead}</p>
          <div className="flex flex-wrap gap-4 mt-9">
            <Link to="/ziadost" data-testid="pro-cta-register" className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-semibold px-7 py-3.5 hover:bg-[#F2E3C6] transition-colors">{t.cta} <ArrowRight size={17} /></Link>
            <Link to="/hall-of-fame" data-testid="pro-cta-hof" className="inline-flex items-center gap-2 border border-[#D4AF37]/60 text-[#D4AF37] px-7 py-3.5 hover:bg-[#D4AF37]/10 transition-colors">{t.cta2}</Link>
          </div>
          <ShareBar lang={lang} title={t.title} />
        </Reveal>

        <h2 className="font-display text-2xl md:text-3xl mt-24 mb-8">{t.stepsTitle}</h2>
        <Steps t={t} />

        <h2 className="font-display text-2xl md:text-3xl mt-24 mb-8">{t.benTitle}</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {t.ben.map(([h, d], i) => {
            const Icon = BEN_ICONS[i];
            return (
              <Reveal key={h} delay={i * 0.08}>
                <div className="border-l-2 border-[#D4AF37] pl-5 py-2" data-testid={`pro-benefit-${i}`}>
                  <Icon size={22} className="text-[#D4AF37] mb-3" />
                  <p className="text-white text-lg font-display">{h}</p>
                  <p className="text-white/55 text-sm mt-2 leading-relaxed">{d}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal>
          <div className="mt-24 border border-[#D4AF37]/40 bg-gradient-to-br from-[#D4AF37]/[0.08] to-transparent p-8 md:p-12 flex flex-col md:flex-row md:items-center gap-8 justify-between" data-testid="pro-final-cta">
            <div className="max-w-2xl">
              <p className="font-display text-2xl md:text-3xl">{t.endTitle}</p>
              <p className="text-white/50 text-sm mt-3 leading-relaxed">{t.fee}</p>
            </div>
            <Link to="/ziadost" data-testid="pro-cta-register-bottom" className="shrink-0 inline-flex items-center gap-2 bg-[#D4AF37] text-black font-semibold px-7 py-3.5 hover:bg-[#F2E3C6] transition-colors">{t.cta} <ArrowRight size={17} /></Link>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
